// Blocos de interface. Sem framework: uma função por bloco, string de HTML.
// Se isto virar difícil de manter, é sinal de que chegou a hora de um
// framework de verdade — e não antes.

export const esc = (s: unknown): string =>
  String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );

/** Cabeçalho de página: título à esquerda, ações à direita. Nada mais.
 *  O app de referência não usa subtítulo nem rótulo de origem — a página se
 *  explica pelo conteúdo, e o cabeçalho fica fora do caminho. */
export function head(opts: { title: string; sub?: string; actions?: string }): string {
  return `
    <header class="page-head">
      <div>
        <h1 class="page-title">${esc(opts.title)}</h1>
        ${opts.sub ? `<p class="page-sub">${esc(opts.sub)}</p>` : ""}
      </div>
      ${opts.actions ? `<div class="page-actions">${opts.actions}</div>` : ""}
    </header>`;
}

export function metric(label: string, value: string, note?: string): string {
  return `
    <div class="card metric">
      <div class="metric-label">${esc(label)}</div>
      <div class="metric-value">${esc(value)}</div>
      ${note ? `<div class="metric-note">${esc(note)}</div>` : ""}
    </div>`;
}

type Tone = "ok" | "run" | "wait" | "crit" | "idle" | "agent" | "human";

export function pill(text: string, tone: Tone = "idle"): string {
  return `<span class="pill" data-tone="${tone}">${esc(text)}</span>`;
}

const TONES: Record<string, Tone> = {
  concluida: "ok", fechada: "ok", sucesso: "ok",
  rodando: "run", revisao: "run",
  aguardando: "wait", pendente: "idle",
  falhou: "crit", falha: "crit",
};

export const stateTone = (s: string): Tone => TONES[s] ?? "idle";

export function table(cols: string[], rows: string[]): string {
  if (!rows.length) return "";
  return `
    <div class="table-wrap">
      <table>
        <thead><tr>${cols.map((c) => `<th>${esc(c)}</th>`).join("")}</tr></thead>
        <tbody>${rows.join("")}</tbody>
      </table>
    </div>`;
}

/** Vazio que ensina o próximo passo, não que só informa a ausência. */
export function empty(title: string, body: string, action?: string): string {
  return `
    <div class="empty">
      <div class="empty-mark">◍</div>
      <div class="empty-title">${esc(title)}</div>
      <p class="empty-body">${esc(body)}</p>
      ${action ?? ""}
    </div>`;
}

/** Erro que diz o que fazer. O `next` vem da origem; nunca mostramos stack. */
export function errorBox(source: string, message: string, next: string): string {
  return `
    <div class="err">
      <div class="err-title">${esc(source)} não respondeu</div>
      <div class="err-next">${esc(message)} — ${esc(next)}</div>
    </div>`;
}

export const skeleton = (n = 4): string => `<div>${'<div class="skeleton"></div>'.repeat(n)}</div>`;

/** Dinheiro no formato do app: $3.99, não US$ 3,99. O gasto é cobrado em
 *  dólar pelo gateway, e ponto decimal é o que se lê num painel técnico. */
export const money = (cents: number): string =>
  "$" + (cents / 100).toFixed(2);

/** Tempo relativo no formato do app de referência: "just now", "5m ago",
 *  "22h ago", "6d ago". É rótulo de UI, e a UI dele é toda em inglês — cada
 *  card mistura este texto com "Once", "Idle" e "running" na mesma linha. */
export function since(iso: string | null): string {
  if (!iso) return "—";
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function duration(from: string, to: string | null): string {
  const ms = (to ? new Date(to).getTime() : Date.now()) - new Date(from).getTime();
  const min = Math.floor(ms / 60000);
  return min < 60 ? `${min}m ${Math.floor((ms % 60000) / 1000)}s` : `${Math.floor(min / 60)}h ${min % 60}m`;
}

let toastTimer: number;
export function toast(message: string): void {
  const el = document.getElementById("toast")!;
  el.textContent = message;
  el.dataset.show = "true";
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => (el.dataset.show = "false"), 2600);
}

/** Abas — o padrão de navegação secundária que aparece em quase toda página
 *  do app: uma faixa com fundo próprio e a aba ativa em relevo. */
export function tabs(itens: Array<{ label: string; href?: string; ativa?: boolean }>): string {
  return `<div class="tabs" role="tablist">${itens
    .map(
      (t) =>
        `<a class="tab" role="tab" aria-selected="${!!t.ativa}" href="${t.href ?? "#"}">${esc(t.label)}</a>`,
    )
    .join("")}</div>`;
}

/** Markdown do agente. Cobre o que ele de fato escreve — tabela, lista
 *  numerada, negrito e código — sem trazer uma biblioteca inteira para isso.
 *  ponytail: se aparecer markdown que isto não cobre, trocar por `marked`. */
export function md(texto: string): string {
  const linhas = esc(texto).split(/\r?\n/);
  const out: string[] = [];
  let lista: string[] = [];
  let tabela: string[][] = [];

  const fechaLista = () => {
    if (lista.length) {
      out.push(`<ol>${lista.map((i) => `<li>${i}</li>`).join("")}</ol>`);
      lista = [];
    }
  };
  const fechaTabela = () => {
    if (tabela.length) {
      const [cab, ...corpo] = tabela;
      out.push(
        `<table><thead><tr>${cab.map((c) => `<th>${c}</th>`).join("")}</tr></thead>` +
          `<tbody>${corpo
            .map((l) => `<tr>${l.map((c) => `<td>${c}</td>`).join("")}</tr>`)
            .join("")}</tbody></table>`,
      );
      tabela = [];
    }
  };

  for (const linha of linhas) {
    const l = linha.trim();
    if (/^\|.*\|$/.test(l)) {
      // Célula também é texto: sem isto, `código` e **negrito** dentro de uma
      // tabela saem crus, com as crases à mostra.
      const cels = l.slice(1, -1).split("|").map((c) => inline(c.trim()));
      if (!cels.every((c) => /^-+$/.test(c))) tabela.push(cels);
      continue;
    }
    fechaTabela();

    const num = l.match(/^(\d+)\.\s+(.*)$/);
    if (num) {
      lista.push(inline(num[2]));
      continue;
    }
    fechaLista();

    if (l) out.push(`<p>${inline(l)}</p>`);
  }
  fechaTabela();
  fechaLista();
  return `<div class="md">${out.join("")}</div>`;
}

const inline = (t: string) =>
  t
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
